import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Link2,
  Unlink,
  Search,
  Plus,
  Check,
  AlertCircle,
  Loader2,
  ExternalLink
} from 'lucide-react'
import { supabase } from '../../supabase-client'

/**
 * XeroCustomerMapping Component
 *
 * Manages linking Odin customers to Xero contacts.
 * Features:
 * - List all customers with mapping status
 * - Search and link to existing Xero contacts
 * - Create new Xero contacts from Odin customers
 * - Fuzzy match suggestions with confidence scores
 * - Unlink functionality
 *
 * @param {Object} props
 * @param {Object} props.user - Current authenticated user
 */
export default function XeroCustomerMapping({ user }) {
  const [mappings, setMappings] = useState([])
  const [unmappedCustomers, setUnmappedCustomers] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [summary, setSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Link modal state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [isLinking, setIsLinking] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  // Debounce timer for search
  const searchTimerRef = useRef(null)

  // Load mappings on mount
  useEffect(() => {
    fetchMappings()
  }, [])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  /**
   * Fetch all customer mappings with suggestions
   */
  const fetchMappings = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setError('Authentication required. Please log in again.')
        setIsLoading(false)
        return
      }

      const { data, error } = await supabase.functions.invoke('xero-contacts', {
        body: { action: 'get-mappings', includeCustomerDetails: true },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to fetch mappings')
      }

      setMappings(data.mappings || [])
      setUnmappedCustomers(data.unmappedCustomers || [])
      setSuggestions(data.suggestions || [])
      setSummary(data.summary || null)
    } catch (err) {
      console.error('Error fetching mappings:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Search Xero contacts by query (actual API call)
   */
  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setError('Authentication required. Please log in again.')
        setIsSearching(false)
        return
      }

      const { data, error } = await supabase.functions.invoke('xero-contacts', {
        body: { action: 'search', query },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Search failed')
      }

      setSearchResults(data.contacts || [])
    } catch (err) {
      console.error('Error searching contacts:', err)
      setError(err.message)
    } finally {
      setIsSearching(false)
    }
  }

  /**
   * Debounced search handler - waits 400ms after user stops typing
   */
  const handleSearchDebounced = useCallback((query) => {
    // Clear existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }

    // Set new timer
    searchTimerRef.current = setTimeout(() => {
      performSearch(query)
    }, 400) // Wait 400ms after user stops typing
  }, [])

  /**
   * Manual search trigger (for search button)
   */
  const handleSearchManual = () => {
    // Clear debounce timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    // Perform search immediately
    performSearch(searchQuery)
  }

  /**
   * Link customer to Xero contact
   */
  const handleLinkToContact = async (xeroContact, mappingType = 'manual', confidenceScore = null) => {
    if (!selectedCustomer) return

    setIsLinking(true)
    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setError('Authentication required. Please log in again.')
        setIsLinking(false)
        return
      }

      const { error } = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'link',
          odinCustomerId: selectedCustomer.id,
          xeroContactId: xeroContact.ContactID,
          xeroContactName: xeroContact.Name,
          mappingType,
          confidenceScore
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to link customer')
      }

      // Refresh mappings
      await fetchMappings()

      // Close modal
      setIsLinkModalOpen(false)
      setSelectedCustomer(null)
      setSearchQuery('')
      setSearchResults([])
    } catch (err) {
      console.error('Error linking customer:', err)
      setError(err.message)
    } finally {
      setIsLinking(false)
    }
  }

  /**
   * Create new Xero contact from Odin customer
   */
  const handleCreateContact = async () => {
    if (!selectedCustomer) return

    setIsCreating(true)
    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setError('Authentication required. Please log in again.')
        setIsCreating(false)
        return
      }

      const { error } = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'create',
          odinCustomerId: selectedCustomer.id
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to create contact')
      }

      // Refresh mappings
      await fetchMappings()

      // Close modal
      setIsLinkModalOpen(false)
      setSelectedCustomer(null)
    } catch (err) {
      console.error('Error creating contact:', err)
      setError(err.message)
    } finally {
      setIsCreating(false)
    }
  }

  /**
   * Unlink customer from Xero contact
   */
  const handleUnlink = async (customerId) => {
    if (!confirm('Are you sure you want to unlink this customer? This will not delete the Xero contact.')) {
      return
    }

    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        setError('Authentication required. Please log in again.')
        return
      }

      const { error } = await supabase.functions.invoke('xero-contacts', {
        body: {
          action: 'unlink',
          odinCustomerId: customerId
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to unlink customer')
      }

      // Refresh mappings
      await fetchMappings()
    } catch (err) {
      console.error('Error unlinking customer:', err)
      setError(err.message)
    }
  }

  /**
   * Open link modal for a customer
   */
  const openLinkModal = (customer) => {
    setSelectedCustomer(customer)
    setSearchQuery(customer.name) // Pre-fill with customer name
    setSearchResults([])
    setIsLinkModalOpen(true)
  }

  /**
   * Get fuzzy match suggestions for a customer
   */
  const getSuggestionsForCustomer = (customer) => {
    const suggestion = suggestions.find(s => s.customer.id === customer.id)
    return suggestion?.suggestions || []
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>Customer Mapping Summary</CardTitle>
            <CardDescription>
              Overview of customer-to-contact mappings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">{summary.totalCustomers}</div>
                <div className="text-sm text-gray-500">Total Customers</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{summary.mappedCount}</div>
                <div className="text-sm text-gray-500">Mapped</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">{summary.unmappedCount}</div>
                <div className="text-sm text-gray-500">Unmapped</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{summary.mappingPercentage}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Unmapped Customers */}
      {unmappedCustomers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Unmapped Customers</CardTitle>
            <CardDescription>
              {unmappedCustomers.length} customer{unmappedCustomers.length !== 1 ? 's' : ''} not yet linked to Xero
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Suggestions</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmappedCustomers.map((customer) => {
                  const customerSuggestions = getSuggestionsForCustomer(customer)
                  const topSuggestion = customerSuggestions[0]

                  return (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell className="text-gray-500">{customer.email || '—'}</TableCell>
                      <TableCell>
                        {topSuggestion ? (
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {topSuggestion.contact.Name}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {Math.round(topSuggestion.confidence * 100)}% match
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No suggestions</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openLinkModal(customer)}
                        >
                          <Link2 className="h-4 w-4 mr-1" />
                          Link to Xero
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Mapped Customers - Collapsed by default */}
      {mappings.length > 0 && (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="mapped-customers" className="border rounded-lg">
            <Card className="border-0">
              <CardHeader className="pb-0">
                <AccordionTrigger className="hover:no-underline py-6">
                  <div className="flex flex-col items-start text-left">
                    <CardTitle>Mapped Customers ({mappings.length})</CardTitle>
                    <CardDescription>
                      Customers linked to Xero contacts
                    </CardDescription>
                  </div>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Odin Customer</TableHead>
                        <TableHead>Xero Contact</TableHead>
                        <TableHead>Mapping Type</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mappings.map((mapping) => (
                        <TableRow key={mapping.id}>
                          <TableCell className="font-medium">
                            {/* Customer name would be fetched via mapping if needed */}
                            Customer ID: {mapping.odin_customer_id.slice(0, 8)}...
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {mapping.xero_contact_name}
                              <Badge variant="secondary" className="text-xs">
                                <Check className="h-3 w-3 mr-1" />
                                Linked
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              mapping.mapping_type === 'manual' ? 'default' :
                              mapping.mapping_type === 'auto_matched' ? 'outline' : 'secondary'
                            }>
                              {mapping.mapping_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {new Date(mapping.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnlink(mapping.odin_customer_id)}
                            >
                              <Unlink className="h-4 w-4 mr-1" />
                              Unlink
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
        </Accordion>
      )}

      {/* Link Customer Modal */}
      <Dialog open={isLinkModalOpen} onOpenChange={setIsLinkModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Link Customer to Xero Contact</DialogTitle>
            <DialogDescription>
              Search for an existing Xero contact or create a new one for {selectedCustomer?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Section */}
            <div className="flex gap-2">
              <Input
                placeholder="Search Xero contacts by name or email..."
                value={searchQuery}
                onChange={(e) => {
                  const newQuery = e.target.value
                  setSearchQuery(newQuery)
                  handleSearchDebounced(newQuery)
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchManual()}
              />
              <Button onClick={handleSearchManual} disabled={isSearching}>
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Search
              </Button>
            </div>

            {/* Fuzzy Match Suggestions */}
            {selectedCustomer && getSuggestionsForCustomer(selectedCustomer).length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Suggested Matches</h4>
                <div className="space-y-2">
                  {getSuggestionsForCustomer(selectedCustomer).map((suggestion) => (
                    <div
                      key={suggestion.contact.ContactID}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{suggestion.contact.Name}</div>
                        <div className="text-sm text-gray-500">
                          {suggestion.contact.EmailAddress || 'No email'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {suggestion.matchReason} • {Math.round(suggestion.confidence * 100)}% confidence
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleLinkToContact(
                          suggestion.contact,
                          'auto_matched',
                          suggestion.confidence
                        )}
                        disabled={isLinking}
                      >
                        {isLinking ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Link2 className="h-4 w-4 mr-1" />
                            Link
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Search Results</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {searchResults.map((contact) => (
                    <div
                      key={contact.ContactID}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{contact.Name}</div>
                        <div className="text-sm text-gray-500">
                          {contact.EmailAddress || 'No email'}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleLinkToContact(contact, 'manual')}
                        disabled={isLinking}
                      >
                        {isLinking ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Link2 className="h-4 w-4 mr-1" />
                            Link
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Create New Contact */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium">Create New Xero Contact</h4>
                <p className="text-sm text-gray-500">
                  Create a new contact in Xero using the customer's details
                </p>
              </div>
              <Button
                onClick={handleCreateContact}
                disabled={isCreating}
              >
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Create & Link
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
